<template>
  <div class="template">
    <TranscriptionsViewWrapper>
      <GoBackButton path="/transcriptions" />
      <LoaderFullPage v-if="isPolling && !transcriptionZoom" />
      <template v-if="transcriptionZoom">
        <TranscriptionZoomHeader v-bind="transcriptionZoom" />
        <div class="content">
          <div class="column speakers scrollable">
            <TranscriptionTimeLine
              v-if="speakersTimeLineData"
              :data="speakersTimeLineData"
              autoScroll
              :currentTime
              :show-content="false"
              @time-changed="handleOnTimeUpdate"
            >
              <template #title>
                <h6 class="title-medium-semibold summary-title">Sommaire</h6>
              </template>
              <template v-if="!speakersTimeLineData.length" #no-data>
                <p>La liste des intervenants n'est pas disponible.</p>
              </template>
            </TranscriptionTimeLine>
          </div>
          <div class="column transcriptions">
            <ResponsiveVideoPlayer
              v-if="transcriptionZoom && transcriptionZoom.stream_url"
              :time="initialTime"
              :videoSrc="transcriptionZoom.stream_url"
              @update-time="handleOnVideoTimeUpdate"
              class="video"
            >
              <template #video-error>
                <p>Veuillez consulter le site de l'institution concernée.</p>
              </template>
            </ResponsiveVideoPlayer>
            <div class="timeline-container">
              <TranscriptionTimeLine
                v-if="transcriptionTimeLineData"
                :data="transcriptionTimeLineData"
                autoScroll
                :currentTime
                @time-changed="handleOnTimeUpdate"
                class="scrollable"
              >
                <template #title>
                  <div class="search-container">
                    <GenericListNavigator
                      v-if="!isEmpty(transcriptionZoom.occurrences)"
                      :list="matchingTimelineElements"
                      isActive
                      title="Parcourir les occurrences"
                      @change="
                        (item: any) => {
                          handleOnTimeUpdate(item.time);
                        }
                      "
                      @close="autoScroll = true"
                      @activate="autoScroll = false"
                    />
                  </div>
                  <div class="timeline-controls">
                    <div class="slider-container">
                      <label for="">Synchroniser la vidéo&nbsp;</label>
                      <SwitchSlide
                        id=""
                        :is-active="autoScroll"
                        @toggle="autoScroll = !autoScroll"
                      />
                    </div>
                    <h6 class="title-small-semibold transcriptions-title">
                      Transcriptions générées par IA
                    </h6>
                  </div>
                </template>
                <template v-if="!transcriptionTimeLineData.length" #no-data>
                  <p>Les transcriptions ne sont pas encore disponibles.</p>
                </template>
              </TranscriptionTimeLine>
            </div>
          </div>
        </div>
      </template>
    </TranscriptionsViewWrapper>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { isEmpty } from "lodash";
import { storeToRefs } from "pinia";
import GenericListNavigator from "@/commons/components/molecules/GenericListNavigator.vue";
import GoBackButton from "@/follaw-fr/components/general/atoms/GoBackButton.vue";
import LoaderFullPage from "@/follaw-fr/components/reusable/molecules/LoaderFullPage.vue";
import ResponsiveVideoPlayer from "@/follaw-fr/components/reusable/molecules/ResponsiveVideoPlayer.vue";
import SwitchSlide from "@/follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import TranscriptionZoomHeader from "@/follaw-fr/components/transcriptions/molecules/TranscriptionZoomHeader.vue";
import TranscriptionTimeLine from "@/follaw-fr/components/transcriptions/molecules/TranscriptionTimeLine.vue";
import TranscriptionsViewWrapper from "@/follaw-fr/components/transcriptions/templates/TranscriptionsViewWrapper.vue";
import { useRoute } from "vue-router";
import { useTranscriptionsStore } from "@/follaw-fr/stores/transcriptions";

const { transcriptionZoom, isPolling } = storeToRefs(useTranscriptionsStore());
const { startPolling, stopPolling, resetZoom } = useTranscriptionsStore();

const route = useRoute();

const autoScroll = ref(true);
const currentTime = ref(0);
const initialTime = ref(Number(route.query.time) || 0);

const speakersTimeLineData = computed(() => {
  if (!transcriptionZoom.value?.speakers) return [];

  return transcriptionZoom.value.speakers.map((speaker) => ({
    speaker: speaker.label,
    time: speaker.timestamp,
  }));
});

const transcriptionTimeLineData = computed(() => {
  if (!transcriptionZoom.value?.transcriptions) return [];

  return transcriptionZoom.value.transcriptions.map((transcription) => ({
    text: transcription.text,
    time: transcription.timestamps.start,
    containsMatches: transcription.contains_matches,
  }));
});

const matchingTimelineElements = computed(() =>
  transcriptionTimeLineData.value.filter((m) => m.containsMatches)
);

const handleOnTimeUpdate = (time: number) => {
  currentTime.value = time;
  initialTime.value = time;
};

const handleOnVideoTimeUpdate = (time: number) => {
  if (autoScroll.value) {
    currentTime.value = time;
  }
};

onMounted(() => {
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
  resetZoom();
});

/** 
 * If 'time' query param specified, then init video player to that time
/* If video is 'ongoing then init to the latest transcription 
 */
watch(
  [transcriptionZoom, () => route.query.time],
  ([newTranscriptionZoom, newTimeQuery]) => {
    if (newTimeQuery) {
      handleOnTimeUpdate(Math.floor(Number(newTimeQuery)));
      return;
    }
    if (newTranscriptionZoom) {
      if (newTranscriptionZoom.ongoing) {
        const lastTranscription =
          newTranscriptionZoom.transcriptions?.at(-1)?.timestamps.start ?? 0;
        handleOnTimeUpdate(lastTranscription);
      } else {
        const firstTranscription =
          newTranscriptionZoom.transcriptions?.at(0)?.timestamps.start ?? 0;
        handleOnTimeUpdate(firstTranscription);
      }

      return;
    }
  },
  { once: true }
);
</script>

<style scoped lang="scss">
.content {
  display: flex;
  gap: 12px;
  height: calc(100vh - 100px);
}

.column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.transcriptions {
  flex: 2;
}

.speakers {
  flex: 1;
}

.video {
  max-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-container {
  height: 100%;
  overflow-y: auto;
}

.timeline-controls {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
}

.slider-container {
  display: flex;
}

.scrollable {
  flex: 1;
  overflow-y: auto;
}

.transcriptions-title {
  color: var(--clr-n6);
}

.summary-title {
  color: var(--clr-s7);
}
</style>
